import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { API_BASE } from '../services/api';
import './OrderStatus.css';

function OrderStatus() {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!orderId) {
      setError('Order ID is required');
      setLoading(false);
      return;
    }

    // Fetch order status
    const fetchOrder = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/orders/${orderId}${email ? `?email=${encodeURIComponent(email)}` : ''}`);
        if (!response.ok) {
          throw new Error('Order not found');
        }
        const data = await response.json();
        if (data.success && data.order) {
          setOrder(data.order);
        } else {
          throw new Error('Invalid order data');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();

    // Poll for updates every 10 seconds
    const interval = setInterval(fetchOrder, 10000);
    return () => clearInterval(interval);
  }, [orderId, email]);

  const getStatusBadge = (status) => {
    const statusClass = status.toLowerCase().replace('_', '-');
    return <span className={`status-badge status-${statusClass}`}>{status}</span>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  const paymentConfirmed = (order?.payment_status || '').toLowerCase() === 'confirmed';
  const deploymentReady = (order?.deployment_status || '').toLowerCase() === 'deployed';
  const fulfillmentDone = (order?.fulfillment_status || '').toLowerCase() === 'fulfilled';

  if (loading) {
    return (
      <div className="order-status-page">
        <div className="loading">Loading order status...</div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="order-status-page">
        <div className="error">
          <h1>Order Not Found</h1>
          <p>{error || 'Unable to load order information.'}</p>
          <p className="order-id">Order ID: {orderId}</p>
          <button
            className="btn btn-primary"
            style={{ marginTop: '1rem' }}
            onClick={() => { setError(null); setLoading(true); window.location.reload(); }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="order-status-page">
      <div className="order-status-container">
        <h1>Order Status</h1>

        <div className="order-info">
          <div className="info-section">
            <h2>Order Details</h2>
            <div className="info-row">
              <span className="label">Order ID:</span>
              <span className="value">{order.order_id}</span>
            </div>
            <div className="info-row">
              <span className="label">Domain:</span>
              <span className="value domain-name">{order.domain}</span>
            </div>
            <div className="info-row">
              <span className="label">Price:</span>
              <span className="value">${order.price.toFixed(2)} {order.currency}</span>
            </div>
            <div className="info-row">
              <span className="label">Package:</span>
              <span className="value">{order.package_type || 'domain_only'}</span>
            </div>
            <div className="info-row">
              <span className="label">Order Date:</span>
              <span className="value">{formatDate(order.created_at)}</span>
            </div>
          </div>

          <div className="status-section">
            <h2>Status</h2>

            <div className="status-item">
              <span className="label">Payment:</span>
              {getStatusBadge(order.payment_status)}
              {order.payment_confirmed_at && (
                <span className="status-date">Confirmed: {formatDate(order.payment_confirmed_at)}</span>
              )}
            </div>

            <div className="status-item">
              <span className="label">Domain Registration:</span>
              {order.domain_registered ? (
                <>
                  {getStatusBadge('Registered')}
                  {order.domain_registration_id && (
                    <span className="status-id">ID: {order.domain_registration_id}</span>
                  )}
                </>
              ) : (
                getStatusBadge('Pending')
              )}
            </div>

            <div className="status-item">
              <span className="label">Deployment:</span>
              {getStatusBadge(order.deployment_status)}
              {order.deployment_url && (
                <a href={order.deployment_url} target="_blank" rel="noopener noreferrer" className="deployment-link">
                  View site <i className="fa-solid fa-arrow-up-right-from-square" aria-hidden="true" />
                </a>
              )}
            </div>

            <div className="status-item">
              <span className="label">Delivery / Transfer:</span>
              {getStatusBadge(order.fulfillment_status || 'pending')}
              {(order.fulfilled_at || order.fulfillment_notes) && (
                <span className="status-date">
                  {order.fulfilled_at ? `Updated: ${formatDate(order.fulfilled_at)}` : 'Awaiting handoff'}
                </span>
              )}
            </div>
          </div>

          {order.notes && (
            <div className="notes-section">
              <h2>Notes</h2>
              <p className="notes-text">{order.notes}</p>
            </div>
          )}
        </div>

        <div className="status-timeline">
          <h2>Order Timeline</h2>
          <div className="timeline">
            <div className={`timeline-item ${paymentConfirmed ? 'completed' : 'pending'}`}>
              <div className="timeline-marker"></div>
              <div className="timeline-content">
                <h3>Payment Confirmed</h3>
                <p>{paymentConfirmed ? 'Payment received and confirmed' : 'Waiting for payment (provisioning is blocked until confirmed)'}</p>
              </div>
            </div>
            <div className={`timeline-item ${paymentConfirmed && order.domain_registered ? 'completed' : 'pending'}`}>
              <div className="timeline-marker"></div>
              <div className="timeline-content">
                <h3>Domain Registration</h3>
                <p>
                  {!paymentConfirmed
                    ? 'Queued (starts only after payment confirmation)'
                    : (order.domain_registered ? 'Domain registered successfully' : 'Domain registration in progress')}
                </p>
              </div>
            </div>
            <div className={`timeline-item ${paymentConfirmed && deploymentReady ? 'completed' : 'pending'}`}>
              <div className="timeline-marker"></div>
              <div className="timeline-content">
                <h3>Site Deployment</h3>
                <p>
                  {!paymentConfirmed
                    ? 'Queued (starts only after payment confirmation)'
                    : (deploymentReady ? 'Site deployed and live' : 'Deployment pending')}
                </p>
              </div>
            </div>
            <div className={`timeline-item ${paymentConfirmed && fulfillmentDone ? 'completed' : 'pending'}`}>
              <div className="timeline-marker"></div>
              <div className="timeline-content">
                <h3>Delivery & Transfer Handoff</h3>
                <p>
                  {!paymentConfirmed
                    ? 'Blocked until payment is confirmed'
                    : (fulfillmentDone
                      ? 'Delivery complete. Transfer handoff instructions are tracked in order notes.'
                      : 'Preparing delivery handoff / transfer details')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OrderStatus;


